import React, { FC, useState, useEffect, useRef, useMemo } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import CurrencySelect from "@/components/forms/selects/CurrencySelect/CurrencySelect";
import {
  ArIcon,
  CalendarCheck,
  ChevronLeft,
  CloseIcon,
  EnIcon,
  Logout,
  MenuIcon,
  Settings,
  User,
} from "@/components/icons";
import { useTranslations } from "next-intl";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/types/RootState";
import { useConfig } from "@/context/ConfigContext";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { AppDispatch } from "@/redux/store";
import {
  Avatar,
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Link from "next/link";
import { destroyCookie, parseCookies } from "nookies";
import { useAuth } from "@/context/AuthContext";
import { getUserProfile } from "@/utils/profile/getUser";

import LangSelect from "@/components/forms/selects/LangSelect/LangSelect";
import { useLocalization } from "@/context/LocalizationContext";
import ReusableDrawer from "../../sideBars/ReusableDrawer/ReusableDrawer";

interface HeaderProps {
  show?: boolean;
  hideYellowHeader?: boolean;
}

const Header: FC<HeaderProps> = ({ show, hideYellowHeader = false }) => {
  const t = useTranslations();
  const router = useRouter();
  const { locale } = router;
  const currentPath = router.pathname;

  const { config } = useConfig();
  const dispatch: AppDispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [openCurrencyDialog, setOpenCurrencyDialog] = useState(false);
  const [openMenuDialog, setOpenMenuDialog] = useState(false);

  const currency =
  config.currency.code ??  useSelector((state: RootState) => state.hotelSearch.currency);
    
  const { isAuthenticated } = useAuth();

  const fetchUserProfile = async () => {
    try {
      await getUserProfile(dispatch);
    } catch (error) {
      console.error("Failed to fetch user profile:", error);
    }
  };
  useEffect(() => {
    if (isAuthenticated) {
      fetchUserProfile();
    }
  }, [isAuthenticated]);

  const isMobile = useMediaQuery("(max-width:576px)");
  const anchor = isMobile ? "bottom" : locale === "ar" ? "left" : "right";
  const { setLocalization } = useLocalization();
  const switchLanguage = (newLocale: string) => {
    router.push({ pathname: router.pathname, query: router.query }, undefined, {
      locale: newLocale,
    });
    setLocalization({
      lang: newLocale as "en" | "ar",
    });
    setOpen(false);
  };

  const navLinks = useMemo(
    () => [
      { label: t("home"), link: "/home" },
      { label: t("flights"), link: "/flights" },
      { label: t("hotels"), link: "/hotels" },
      { label: t("offers"), link: "/offers" },
      { label: t("umrah-services"), link: "/umrah-services" },
      { label: t("contact-us"), link: "/contact-us" },
    ],
    [t]
  );

  const data = {
    logo: "/images/logo-Almuayed.png",
    navLinks: navLinks,
    profileImage: { initials: "BG" },
    
    // currency: currency,
    // language: locale === "en" ? "العربية" : "English",
  };

  const currencies = config.config.locals?.currency.map(
    (currency: {
      currency_code: string;
      currency_name: string;
      currency_name_ar: string;
      symbol: string;
    }) => ({
      label:
        locale === "ar" ? currency.currency_name_ar : currency.currency_name,
      value: currency.currency_code,
      symbol: currency.symbol,
    })
  );

  // const [selectedCurrency, setSelectedCurrency] = useState(data.currency);

  const [currencyDropdown, setCurrencyDropdown] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const handleCurrencyChange = (value: string) => {
    // setCurrency(value);
    dispatch(setSearchValues({ currency: value }));
    setLocalization({
      currency: value,
    });
  };

  const toggleDropdown = () => {
    setCurrencyDropdown((prevState) => !prevState);
  };
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setCurrencyDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   setSelectedCurrency(event.target.value);
  // };

  const [openMenu, setOpenMenu] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpenMenu((prev) => !prev);
  };

  const handleClose = () => {
    setOpenMenu(false);
  };
  const menuItems = useMemo(() => {
    const items = [
      { icon: <User />, label: t("profile"), link: "/profile" },
      { icon: <CalendarCheck />, label: t("myBookings"), link: "/bookings" },
      { icon: <Settings />, label: t("settings"), link: "/settings" },
    ];
    return isMobile ? items.filter((item) => item.link !== "/bookings") : items;
  }, [t, isMobile]);

  const handleNavigate = async (path: string) => {
    setOpen(false);
    setOpenMenuDialog(false);
    setOpenMenu(false);
    await router.push({ pathname: path }, undefined, { locale });
  };
  const { logout } = useAuth();

  const handleLogout = async () => {
    handleClose();
    destroyCookie(null, "next-auth.session-token", {
      path: "/",
    });

    const cookies = parseCookies();
    const cookieNames = Object.keys(cookies);

    cookieNames.forEach((cookieName) => {
      destroyCookie(null, cookieName, {
        path: "/",
        domain:
          process.env.NEXT_PUBLIC_COOKIE_DOMAIN || window.location.hostname,
      });
    });
    const googleCookies = ["G_AUTHUSER_HASH", "G_ENABLED_IDPS"];
    const domain = window.location.hostname;
    googleCookies.forEach((cookieName) => {
      destroyCookie(null, cookieName, {
        path: "/",
        domain: domain,
        secure: false,
      });
    });

    logout();

    await router.push("/signin", undefined, { locale });
  };

  const handleNavigateToProfile = () => {
    router.push("/profile", undefined, { locale });
  };

  const handleNavigateToLogin = () => {
    setOpen(false);
    setOpenMenuDialog(false);
    router.push("/signin", undefined, { locale });
  };

  useEffect(() => {
    if (openMenu) {
      document.body.style.overflow = "hidden";
      const handleScroll = () => {
        handleClose();
        window.removeEventListener("scroll", handleScroll);
      };
      window.addEventListener("scroll", handleScroll);

      return () => {
        document.body.style.overflow = "";
        window.removeEventListener("scroll", handleScroll);
      };
    } else {
      document.body.style.overflow = "";
    }
  }, [openMenu]);

  const disabledPaths = [
    "/signin",
    "/signup",
    "/login-confirm",
    "/reset-password",
    "/confirm-password",
    "/verify-password",
    "/sessionExpired",
  ];

  const isDisabledPath = disabledPaths.includes(currentPath);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        router.pathname === "/search"
          ? `transition-all duration-300 ease-in-out transform ${
              show ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
            }`
          : "!relative !transition-none"
      }
          c-lg:border-b-[0.8px] font-montserrat c-lg:border-white !bg-white bg-opacity-90 backdrop-blur-[20px]`}
    >
      {/* {!hideYellowHeader && (
        <div className="w-full bg-primary  flex items-center justify-center c-lg:h-12 h-10">
          <div className="  text-white c-lg:text-base  text-xs max-c-lg:!text-center text-wrap">
            <span className="font-bold">Almuayed - </span>
            <span className="font-medium">{t(`almuayedHeader`)}</span>
          </div>
        </div>
      )} */}

      <div
        className={`max-w-[1340px] mx-auto c-lg:mx-auto px-4 my-0 z-20 flex c-lg:flex-row  ${
          isDisabledPath && !isAuthenticated ? "" : "flex-row-reverse"
        }  items-center justify-between  h-[95px]  `}
      >
        {isAuthenticated ? (
          <div className="c-lg:hidden">
            <IconButton
              ref={anchorRef}
              onClick={handleNavigateToProfile}
              size="small"
              aria-controls={open ? "account-dropdown" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              aria-label="Account settings"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: "#1E2F56" }} />
            </IconButton>
          </div>
        ) : (
          <React.Fragment>
            {isDisabledPath ? (
              <Link
                href="/home"
                passHref
                locale={locale}
                className="c-lg:hidden"
              >
                <ChevronLeft
                  className="!w-5 !h-5 mt-1 rtl:scale-[-1] "
                  color="#bc9a20"
                />
              </Link>
            ) : (
              <button
                onClick={handleNavigateToLogin}
                className="c-lg:hidden  text-white !bg-primary px-3 py-2 rounded   flex   justify-center items-center gap-2   text-sm font-medium leading-6"
              >
                {t("login")}
              </button>
            )}
          </React.Fragment>
        )}

        <Link
          href="/home"
          locale={locale}
          passHref
          className="focus:outline-none max-c-lg:absolute max-c-lg:left-1/2 max-c-lg:-translate-x-[95px] "
          aria-label="Go to home"
        >
          <Image
            src="/images/logo-Almuayed.png"
            className="w-auto max-h-[80px] max-c-lg:max-w-[155px] "
            alt="almuayed"
            width={215}
            height={36}
            priority
          />
        </Link>

        <div className=" c-lg:flex hidden justify-between w-3/4 ps-20 items-center ">
          {/* <div
            ref={dropdownRef}
            onClick={() => toggleDropdown()}
            className="border-transparent border-b-[3px] hover:border-secondary"
          >
            <CurrencySelect
              options={currencies}
              value={currency}
              onChange={handleCurrencyChange}
              currency
              labelClassName="text-[#1E293B] !text-base font-medium  "
              hasArrow
            />
          </div> */}

          {/* <div
            ref={dropdownRef}
            onClick={() => toggleDropdown()}
            className="border-transparent border-b-[3px] hover:border-secondary"
          >
            <LangSelect
              options={[
                { value: "en", icon: <EnIcon />, label: "English" },
                { value: "ar", icon: <ArIcon />, label: "العربية" },
              ]}
              onChange={(val) => switchLanguage(val)}
              value={locale}
            />
          </div> */}
<div className=" c-lg:flex hidden gap-8 items-center ">

          {data.navLinks.map((link) => {
            const isActive = router.pathname === link.link;
            return (
              <Link
                key={link.link}
                href={{
                  pathname: link.link,
                }}
                locale={locale}
                className={`text-[#1E293B] text-sm leading-6 font-medium  border-transparent border-b-[3px] 
                  ${
                    isActive ? "!border-secondary" : "hover:border-secondary"
                  } `}
              >
                {link.label}
              </Link>
            );
          })}
</div>

          {isAuthenticated ? (
            <Box>
              <React.Fragment>
                <IconButton
                  ref={anchorRef}
                  onClick={handleToggle}
                  size="small"
                  aria-controls={open ? "account-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  className="z-[99999]"
                >
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "#1E2F56" }} />
                </IconButton>

                <Menu
                  id="account-menu"
                  anchorEl={anchorRef.current}
                  open={openMenu}
                  onClose={() => setOpenMenu(false)}
                  onClick={(e) => e.stopPropagation()}
                  disableScrollLock
                  marginThreshold={0}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        minWidth: 240,
                        borderRadius: "0.5rem",
                        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.05)",
                        transform: "none !important",
                      },
                    },
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: router.locale !== "ar" ? "right" : "left",
                  }}
                  anchorOrigin={{
                    horizontal: router.locale !== "ar" ? "right" : "left",
                    vertical: "bottom",
                  }}
                >
                  {menuItems.map((item) => (
                    <MenuItem
                      key={item.link}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNavigate(item.link);
                      }}
                      sx={{
                        py: 1.5,
                        width: "100%",
                        "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: "36px" }}>
                        {item.icon}
                      </ListItemIcon>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.primary", fontWeight: 500 }}
                      >
                        {item.label}
                      </Typography>
                    </MenuItem>
                  ))}

                  <Divider sx={{ my: "4px!important" }} />

                  <MenuItem
                    onClick={handleLogout}
                    sx={{
                      py: 1.5,
                      // px: 2,
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "36px" }}>
                      <Logout />
                    </ListItemIcon>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", fontWeight: 500 }}
                    >
                      {t("logout")}
                    </Typography>
                  </MenuItem>
                </Menu>
              </React.Fragment>
            </Box>
          ) : (
            <Link
              href="/signin"
              locale={locale}
              passHref
              className="rounded text-white !bg-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-secondary flex px-8 py-2 justify-center items-center gap-2 text-base font-medium"
              title="Go to login"
              aria-label="Go to login"
              id="signin-link"
            >
              {t("login")}
            </Link>
          )}
        </div>
        <div
          className="flex c-lg:hidden items-center relative z-50   "
          ref={dropdownMenuRef}
        >
          {!isDisabledPath && (
            <React.Fragment>
              <button onClick={() => setOpen(true)} className="w-full">
                <MenuIcon />
              </button>
              <ReusableDrawer
                open={open}
                onClose={() => setOpen(false)}
                anchor={anchor}
                isMobile={isMobile}
              >
                <div className="flex flex-col items-start gap-4  py-6 bg-[#F7FAFD] min-w-[360px] !h-full !min-h-fit">
                  <button
                    onClick={() => setOpen(false)}
                    className="flex justify-end w-full px-4"
                  >
                    <CloseIcon size="sm" className="text-[#003d6b]" />
                  </button>
                  <Divider className="!border-gray-200 border-2  my-4 font-normal w-full" />

                  <div className="flex flex-col items-start  w-full !h-full !min-h-fit">
                    <div className="flex flex-col items-start gap-1 px-4  w-full ">
                      {!isAuthenticated && (
                        <React.Fragment>
                          <button
                            onClick={handleNavigateToLogin}
                            className="rounded-[24px] text-[#003d6b] w-full border-[1px] border-[#003d6b] flex px-6 py-2 justify-center items-center gap-2   text-sm font-medium leading-6"
                          >
                            {t("login")}
                          </button>

                          <Divider className="!border-[#E7F0F9] border-2  !mt-3  font-normal w-full" />
                        </React.Fragment>
                      )}

                      {data.navLinks.map((link) => {
                        const isActive = router.pathname === link.link;
                        return (
                          <div
                            key={`${link.link}-sm`}
                            className={`!w-full flex flex-col items-start gap-2`}
                            onClick={() => {
                              handleNavigate(link.link);
                            }}
                          >
                            <Link
                              href={{
                                pathname: link.link,
                              }}
                              onClick={() => {
                                if (isActive) {
                                  setOpen(false);
                                }
                              }}
                              locale={locale}
                              className={`text-[#1E293B] text-sm leading-6 font-medium h-10 flex items-center justify-between w-full   
                             `}
                            >
                              <React.Fragment>
                                {link.label}
                                <ChevronLeft
                                  className="w-[16px] h-[16px] ltr:scale-[-1]"
                                  color="#003d6b"
                                />
                              </React.Fragment>
                            </Link>

                            <Divider className="!border-[#E7F0F9] border-2  my-2  font-normal w-full" />
                          </div>
                        );
                      })}

                      <button
                        onClick={() => setOpenCurrencyDialog(true)}
                        className="text-[#1E293B] text-sm leading-6 w-full font-medium mt-2 flex items-center justify-between"
                      >
                        <span>{t("currency")}</span>

                        <span>{currency}</span>
                      </button>

                      <Divider className="!border-[#E7F0F9] border-2  !my-3  font-normal w-full" />

                      <button
                        onClick={() =>
                          switchLanguage(locale === "en" ? "ar" : "en")
                        }
                        className="text-[#1E293B] text-[16px] font-medium leading-normal me-6 w-full flex items-center justify-between"
                      >
                        <span className="font-medium text-sm text-slate-800">
                          {t("language")}
                        </span>
                        <span className="ms-2">
                          {locale === "en" ? "العربية" : "English"}
                        </span>
                      </button>
                      {isAuthenticated && (
                        <React.Fragment>
                          <div className="mt-6 mb-3 font-bold text-lg ">
                            {t(`My account`)}{" "}
                          </div>

                          <div className="flex flex-col items-start justify-center w-full ">
                            {menuItems.map((item) => (
                              <React.Fragment>
                                <button
                                  key={item.link}
                                  onClick={() => {
                                    handleNavigate(item.link);
                                  }}
                                  className={`text-[#1E293B] text-sm   !border-[#E7F0F9] border-b-[1px] leading-6 font-medium h-[52px] flex items-center justify-between w-full `}
                                >
                                  <span className="font-medium text-sm text-slate-800 ">
                                    {item.label}
                                  </span>
                                  <ChevronLeft
                                    className="w-[16px] h-[16px] ltr:scale-[-1]"
                                    color="#003d6b"
                                  />
                                </button>
                              </React.Fragment>
                            ))}
                          </div>

                          <button
                            onClick={handleLogout}
                            className="flex justify-between items-center w-full gap-2  py-2 hover:bg-slate-50 transition-colors"
                          >
                            <span className="font-medium text-sm text-slate-800  ">
                              {t("logout")}
                            </span>
                            <Logout />
                          </button>
                        </React.Fragment>
                      )}
                    </div>
                  </div>
                </div>
              </ReusableDrawer>

              {/* <ReusableDrawer
                open={openCurrencyDialog}
                onClose={() => setOpenCurrencyDialog(false)}
                anchor={anchor}
                isMobile={isMobile}
              >
                <div className="flex flex-col items-start gap-4  py-6 bg-[#F7FAFD] min-w-[360px] !h-full !min-h-fit">
                  <div className="flex items-center justify-between px-4 w-full">
                    <button
                      onClick={() => {
                        setOpenCurrencyDialog(false);
                        setSelectedCurrency(currency);
                      }}
                      className=""
                    >
                      <CloseIcon size="sm" className="text-[#003d6b]" />
                    </button>
                    <h1 className="font-normal text-[#003d6b] text-base">
                      {t("Currencies")}
                    </h1>
                    <button
                      onClick={() => {
                        setOpenCurrencyDialog(false);
                        handleCurrencyChange(selectedCurrency);
                      }}
                      className="text-secondary text-sm leading-6 font-medium"
                    >
                      {t("Apply")}
                    </button>
                  </div>
                  <Divider className="!border-gray-200 border-2  my-4 font-normal w-full" />

                  <div className="flex flex-col items-start  w-full !h-full !min-h-fit">
                    <RadioGroup
                      value={selectedCurrency}
                      onChange={handleChange}
                      name="currency"
                      className={`w-full px-4`}
                    >
                      {currencies.map(
                        (option: {
                          label: string;
                          value: string;
                          symbol: string;
                        }) => (
                          <FormControlLabel
                            key={option.value}
                            value={option.value}
                            control={
                              <Radio
                                sx={{
                                  color: "#bc9a20",
                                  "& .MuiSvgIcon-root": {
                                    fontSize: 24,
                                    color: "#bc9a20",
                                  },
                                }}
                              />
                            }
                            label={
                              currency ? (
                                <div className="flex items-center ">
                                  <span className="ms-2 font-semibold">
                                    {option.symbol}
                                  </span>
                                  <span className="ms-2">{option.label}</span>
                                </div>
                              ) : (
                                option.label
                              )
                            }
                            className={`gap-2 flex items-center`}
                          />
                        )
                      )}
                    </RadioGroup>
                  </div>
                </div>
              </ReusableDrawer> */}

              <ReusableDrawer
                open={openMenuDialog}
                onClose={() => setOpenMenuDialog(false)}
                anchor={anchor}
                isMobile={isMobile}
              >
                <div className="flex flex-col items-start gap-4  py-6 bg-[#F7FAFD] min-w-[360px] !h-full !min-h-fit">
                  <button
                    onClick={() => setOpenMenuDialog(false)}
                    className="flex justify-end w-full px-4"
                  >
                    <CloseIcon size="sm" className="text-[#003d6b]" />
                  </button>
                  <Divider className="!border-gray-200 border-2  my-4 font-normal w-full" />
                  <div className="flex flex-col items-start gap-2 px-0 py-2 w-full rounded-lg  ">
                    <div className="flex flex-col items-start w-full gap-2">
                      {menuItems.map((item) => (
                        <button
                          key={item.link}
                          onClick={() => handleNavigate(item.link)}
                          className="flex items-center w-full gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
                        >
                          {item.icon}
                          <span className="font-medium text-sm text-slate-800 ">
                            {item.label}
                          </span>
                        </button>
                      ))}
                    </div>

                    <div className="w-full border-t border-gray-200 my-1" />
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
                    >
                      <Logout />
                      <span className="font-medium text-sm text-slate-800  ">
                        {t("logout")}
                      </span>
                    </button>
                  </div>
                </div>
              </ReusableDrawer>
            </React.Fragment>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
